import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  ArrowRight, Calculator, Check, ChevronDown, ChevronLeft, ChevronRight, Clock, Mail, Menu, MessageCircle, X,
} from 'lucide-react';
import medixLogo from '../../img/logo.png';
import { APP_URL, CONTACT, whatsappLink } from './landingConfig';
import {
  ANNUAL_MONTHS_PAID, BEFORE_AFTER, COMPARISON, FAQ, FEATURE_TABS, HERO_SLIDES, HERO_WORDS, PILOT_STEPS, PLANS,
  SPECIALTIES, STATS, TRUST,
} from './landingData';
import { Mock } from './LandingMocks';
import ContactForm from './ContactForm';
import ParticlesBackground from './ParticlesBackground';
import { SPECIALTY_PAGES } from './specialtyData';
import usePageMeta from './usePageMeta';
import './Landing.css';

const clp = (n) => '$' + Math.round(n).toLocaleString('es-CL');

// Nota: las animaciones funcionan siempre, también con "reducir movimiento" (decisión del negocio).

// Agrega la clase lp-in cuando el elemento entra en pantalla (animación de aparición).
export const useReveal = () => {
  useEffect(() => {
    const els = document.querySelectorAll('.lp-reveal');
    if (!('IntersectionObserver' in window)) {
      els.forEach((el) => el.classList.add('lp-in'));
      return undefined;
    }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) {
          e.target.classList.add('lp-in');
          io.unobserve(e.target);
        }
      });
    }, { threshold: 0.12 });
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);
};

export const Header = ({ solid = false }) => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  return (
    <header className={solid || scrolled || open ? 'lp-header lp-solid' : 'lp-header'}>
      <div className="lp-container lp-header-inner">
        <a href="/" className="lp-brand" aria-label="Medix, inicio">
          <img src={medixLogo} alt="Medix, gestión médica" />
        </a>
        <nav className={open ? 'lp-nav lp-open' : 'lp-nav'} onClick={() => setOpen(false)}>
          <a href="/#funciones">Funciones</a>
          <a href="/#especialidades">Especialidades</a>
          <a href="/#planes">Planes</a>
          <a href="/#faq">Preguntas</a>
          <a href={APP_URL} className="lp-nav-login">Ingresar</a>
          <a href="/#contacto" className="lp-btn lp-btn-primary lp-btn-sm">Solicitar demo</a>
        </nav>
        <button className="lp-nav-toggle" aria-label="Menú" aria-expanded={open} onClick={() => setOpen(!open)}>
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>
    </header>
  );
};

const RotatingWord = () => {
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setI((x) => (x + 1) % HERO_WORDS.length), 2400);
    return () => clearInterval(t);
  }, []);
  return (
    <span className="lp-rotating" aria-live="polite">
      <span key={i} className="lp-word">{HERO_WORDS[i]}</span>
    </span>
  );
};

// Slider genérico: autoplay, flechas, puntos, swipe y pausa al pasar el mouse.
const Slider = ({ slides, interval = 4500 }) => {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const touchX = useRef(null);
  const count = slides.length;
  const go = useCallback((n) => setIndex((n + count) % count), [count]);

  useEffect(() => {
    if (paused) return undefined;
    const t = setTimeout(() => go(index + 1), interval);
    return () => clearTimeout(t);
  }, [index, paused, interval, go]);

  return (
    <div
      className="lp-slider"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={(e) => { touchX.current = e.touches[0].clientX; setPaused(true); }}
      onTouchEnd={(e) => {
        const dx = e.changedTouches[0].clientX - (touchX.current ?? 0);
        if (Math.abs(dx) > 40) go(index + (dx < 0 ? 1 : -1));
        setPaused(false);
      }}
      aria-roledescription="carrusel"
    >
      <div className="lp-viewport">
        <div className="lp-slides" style={{ transform: `translateX(calc(${-index} * (100% + 48px)))` }}>
          {slides.map((s, i) => (
            <div key={s.caption} className="lp-slide" aria-hidden={i !== index}>
              <Mock name={s.mock} />
            </div>
          ))}
        </div>
      </div>
      <div className="lp-slider-foot">
        <button type="button" onClick={() => go(index - 1)} aria-label="Anterior"><ChevronLeft size={18} /></button>
        <p>{slides[index].caption}</p>
        <button type="button" onClick={() => go(index + 1)} aria-label="Siguiente"><ChevronRight size={18} /></button>
      </div>
      <div className="lp-dots">
        {slides.map((s, i) => (
          <button
            key={s.caption}
            type="button"
            className={i === index ? 'lp-dot lp-active' : 'lp-dot'}
            onClick={() => go(i)}
            aria-label={`Ver ${s.caption}`}
          >
            {i === index && !paused && (
              <span className="lp-dot-fill" style={{ animationDuration: `${interval}ms` }} key={index} />
            )}
          </button>
        ))}
      </div>
    </div>
  );
};

const Hero = () => (
  <section className="lp-hero">
    <div className="lp-hero-glow" aria-hidden="true" />
    <div className="lp-aurora" aria-hidden="true"><i /><i /><i /></div>
    <ParticlesBackground />
    <div className="lp-container lp-hero-grid">
      <div className="lp-hero-copy">
        <p className="lp-pill"><Clock size={14} /> Implementación en 48 horas</p>
        <h1>
          Tu clínica ordenada,<br />
          desde la <RotatingWord />
        </h1>
        <p className="lp-lead">
          Agenda, pacientes, fichas clínicas, consentimientos, recetas e informes en un solo sistema.
          Hecho en Chile para consultas y clínicas.
        </p>
        <div className="lp-hero-actions">
          <a href="/#contacto" className="lp-btn lp-btn-primary lp-btn-lg lp-shine">Solicitar piloto de 30 días <ArrowRight size={18} /></a>
          <a href={whatsappLink()} className="lp-btn lp-btn-glass lp-btn-lg" target="_blank" rel="noreferrer">
            <MessageCircle size={18} /> WhatsApp
          </a>
        </div>
        <ul className="lp-hero-checks">
          <li><Check size={16} /> Sin instalar nada</li>
          <li><Check size={16} /> Sin permanencia</li>
          <li><Check size={16} /> Datos en Chile</li>
        </ul>
      </div>
      <div className="lp-hero-visual">
        <Slider slides={HERO_SLIDES} />
      </div>
    </div>
  </section>
);

// Número que sube desde 0 cuando entra en pantalla.
const CountUp = ({ value, prefix = '', suffix = '' }) => {
  const ref = useRef(null);
  const [n, setN] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el || value === 0) return undefined;
    let raf;
    const io = new IntersectionObserver(([e]) => {
      if (!e.isIntersecting) return;
      io.disconnect();
      const t0 = performance.now();
      const tick = (t) => {
        const k = Math.min(1, (t - t0) / 1400);
        setN(Math.round(value * (1 - Math.pow(1 - k, 3))));
        if (k < 1) raf = requestAnimationFrame(tick);
      };
      raf = requestAnimationFrame(tick);
    }, { threshold: 0.4 });
    io.observe(el);
    return () => { io.disconnect(); cancelAnimationFrame(raf); };
  }, [value]);
  return <span ref={ref}>{prefix}{n}{suffix}</span>;
};

const Stats = () => (
  <section className="lp-stats" aria-label="Medix en cifras">
    <div className="lp-container lp-stats-grid">
      {STATS.map((s) => (
        <div key={s.label} className="lp-stat lp-reveal">
          <strong><CountUp value={s.value} prefix={s.prefix} suffix={s.suffix} /></strong>
          <span>{s.label}</span>
        </div>
      ))}
    </div>
  </section>
);

const BeforeAfter = () => (
  <section className="lp-section lp-section-alt">
    <div className="lp-container">
      <div className="lp-heading lp-reveal">
        <p className="lp-kicker">El cambio</p>
        <h2>Así cambia el día a día de tu clínica</h2>
        <p className="lp-section-lead">Menos tiempo en papeles y más tiempo para tus pacientes.</p>
      </div>
      <div className="lp-ba">
        <div className="lp-ba-col lp-ba-before lp-reveal">
          <h3><X size={18} /> Sin Medix</h3>
          <ul>{BEFORE_AFTER.map((r) => <li key={r.before}>{r.before}</li>)}</ul>
        </div>
        <div className="lp-ba-arrow" aria-hidden="true"><ArrowRight size={28} /></div>
        <div className="lp-ba-col lp-ba-after lp-spot lp-reveal">
          <h3><Check size={18} /> Con Medix</h3>
          <ul>{BEFORE_AFTER.map((r) => <li key={r.after}>{r.after}</li>)}</ul>
        </div>
      </div>
    </div>
  </section>
);

const Mark = ({ v }) => {
  if (v === true) return <span className="lp-yes"><Check size={16} /></span>;
  if (v === false) return <span className="lp-no"><X size={16} /></span>;
  return <span className="lp-partial">{v}</span>;
};

const Comparison = () => (
  <section className="lp-section">
    <div className="lp-container lp-compare-wrap">
      <div className="lp-heading lp-reveal">
        <p className="lp-kicker">Comparación</p>
        <h2>Papel y Excel vs. Medix</h2>
        <p className="lp-section-lead">Lo que ganas al pasar tu clínica a un sistema hecho para la salud.</p>
      </div>
      <div className="lp-compare lp-spot lp-reveal" role="table">
        <div className="lp-compare-row lp-compare-head" role="row">
          <span role="columnheader" />
          <span role="columnheader">Papel y Excel</span>
          <span role="columnheader" className="lp-compare-medix">Medix</span>
        </div>
        {COMPARISON.map((r) => (
          <div key={r.feature} className="lp-compare-row" role="row">
            <span role="cell">{r.feature}</span>
            <span role="cell"><Mark v={r.paper} /></span>
            <span role="cell" className="lp-compare-medix"><Mark v={r.medix} /></span>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const Marquee = () => (
  <section className="lp-marquee" aria-label="Especialidades">
    <div className="lp-marquee-track">
      {[...SPECIALTIES, ...SPECIALTIES].map((s, i) => <span key={i} aria-hidden={i >= SPECIALTIES.length}>{s}</span>)}
    </div>
  </section>
);

const FeatureTour = () => {
  const [active, setActive] = useState(0);
  const tab = FEATURE_TABS[active];
  return (
    <section id="funciones" className="lp-section">
      <div className="lp-container">
        <div className="lp-heading lp-reveal">
          <p className="lp-kicker">Todo en un solo lugar</p>
          <h2>Lo que tu equipo usa a diario, conectado</h2>
          <p className="lp-section-lead">Recepción, profesionales y administración trabajando sobre la misma información.</p>
        </div>
        <div className="lp-tabs lp-reveal" role="tablist">
          {FEATURE_TABS.map((t, i) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={i === active}
              className={i === active ? 'lp-tab lp-active' : 'lp-tab'}
              onClick={() => setActive(i)}
            >
              <t.icon size={18} /> {t.label}
            </button>
          ))}
        </div>
        <div className="lp-tour lp-fade" key={tab.id}>
          <div className="lp-tour-copy">
            <h3>{tab.title}</h3>
            <p>{tab.text}</p>
            <ul>
              {tab.bullets.map((b) => <li key={b}><Check size={18} /> {b}</li>)}
            </ul>
          </div>
          <div className="lp-tour-visual">
            <Mock name={tab.mock} />
          </div>
        </div>
      </div>
    </section>
  );
};

const SpecialtyCards = () => (
  <section id="especialidades" className="lp-section">
    <div className="lp-container">
      <div className="lp-heading lp-reveal">
        <p className="lp-kicker">Por especialidad</p>
        <h2>Medix se adapta a tu especialidad</h2>
        <p className="lp-section-lead">Una base común que sirve a cualquier consulta, y módulos propios para cada área.</p>
      </div>
      <div className="lp-spec-grid">
        {SPECIALTY_PAGES.map((s, i) => (
          <a key={s.slug} href={`/${s.slug}`} className="lp-card lp-spec-card lp-spot lp-reveal" style={{ transitionDelay: `${i * 60}ms` }}>
            <span className="lp-feature-icon"><s.icon size={22} /></span>
            <h3>{s.name}</h3>
            <p>{s.modules.length ? s.modules.map((m) => m.name).slice(0, 3).join(' · ') : 'Agenda, fichas a tu medida, consentimientos y recetas'}</p>
            <span className="lp-spec-more">Ver más <ArrowRight size={16} /></span>
          </a>
        ))}
      </div>
    </div>
  </section>
);

const Trust = () => (
  <section id="seguridad" className="lp-section lp-dark">
    <ParticlesBackground density={0.55} linkDistance={120} />
    <div className="lp-container lp-above">
      <div className="lp-heading lp-reveal">
        <p className="lp-kicker">Pensado para la salud</p>
        <h2>Seguro, ordenado y a la medida de tu especialidad</h2>
        <p className="lp-section-lead">
          Una base común para cualquier especialidad, y módulos propios de cada una, como el odontograma en odontología.
        </p>
      </div>
      <div className="lp-trust-grid">
        {TRUST.map(({ icon: Icon, title, text }, i) => (
          <article key={title} className="lp-trust lp-spot lp-reveal" style={{ transitionDelay: `${i * 60}ms` }}>
            <span className="lp-trust-icon"><Icon size={22} /></span>
            <h3>{title}</h3>
            <p>{text}</p>
          </article>
        ))}
      </div>
    </div>
  </section>
);

const planCost = (plan, people) => {
  if (people > plan.included && !plan.extraUser) return null; // plan no admite usuarios adicionales
  return plan.monthly + Math.max(0, people - plan.included) * (plan.extraUser || 0);
};

const Pricing = () => {
  const [annual, setAnnual] = useState(false);
  const [people, setPeople] = useState(3);
  const factor = annual ? ANNUAL_MONTHS_PAID / 12 : 1;
  const costs = PLANS.map((p) => planCost(p, people));
  const valid = costs.filter((c) => c !== null);
  const best = valid.length ? Math.min(...valid) : null;

  return (
    <section id="planes" className="lp-section lp-section-alt">
      <div className="lp-container">
        <div className="lp-heading lp-reveal">
          <p className="lp-kicker">Planes</p>
          <h2>Precios claros, sin sorpresas</h2>
          <p className="lp-section-lead">Precios en pesos chilenos, más IVA. Plan mensual sin permanencia.</p>
        </div>

        <div className="lp-billing lp-reveal" role="radiogroup" aria-label="Periodo de pago">
          <button type="button" role="radio" aria-checked={!annual} className={!annual ? 'lp-active' : ''} onClick={() => setAnnual(false)}>Mensual</button>
          <button type="button" role="radio" aria-checked={annual} className={annual ? 'lp-active' : ''} onClick={() => setAnnual(true)}>
            Anual <span className="lp-save">2 meses gratis</span>
          </button>
        </div>

        <div className="lp-plan-grid">
          {PLANS.map((p, i) => (
            <article key={p.name} className={`lp-card lp-plan lp-spot lp-reveal${p.featured ? ' lp-featured' : ''}`} style={{ transitionDelay: `${i * 80}ms` }}>
              {p.featured && <span className="lp-badge">Más elegido</span>}
              <h3>{p.name}</h3>
              <p className="lp-plan-audience">{p.audience}</p>
              <p className="lp-plan-price">
                {clp(p.monthly * factor)}<span>/mes + IVA</span>
              </p>
              <p className="lp-plan-note">
                {annual ? `${clp(p.monthly * ANNUAL_MONTHS_PAID)} al año, pago anual` : 'Pago mensual'}
              </p>
              <ul>
                <li><Check size={16} /> {p.included === 1 ? '1 usuario' : `${p.included} usuarios incluidos`}</li>
                {p.extraUser && <li><Check size={16} /> Usuario adicional {clp(p.extraUser)}</li>}
                <li><Check size={16} /> {p.setup ? `Implementación ${clp(p.setup)}` : 'Sin costo de implementación'}</li>
                <li><Check size={16} /> Piloto de 30 días: {clp(p.pilot)}</li>
              </ul>
              <a href="/#contacto" className={p.featured ? 'lp-btn lp-btn-primary' : 'lp-btn lp-btn-ghost'}>Empezar piloto</a>
              <a
                className="lp-plan-wa"
                href={whatsappLink(`Hola, me interesa el plan ${p.name} de Medix. ¿Me pueden contar más?`)}
                target="_blank"
                rel="noreferrer"
              >
                <MessageCircle size={15} /> Consultar por WhatsApp
              </a>
            </article>
          ))}
        </div>

        <div className="lp-calc lp-reveal">
          <div className="lp-calc-head">
            <Calculator size={20} />
            <div>
              <h3>¿Cuánto pagarías tú?</h3>
              <p>Mueve el control según cuántas personas usarán Medix.</p>
            </div>
          </div>
          <a
            className="lp-calc-wa"
            href={whatsappLink(`Hola, somos ${people} ${people === 1 ? 'persona' : 'personas'} en la clínica. ¿Qué plan de Medix nos conviene?`)}
            target="_blank"
            rel="noreferrer"
          >
            <MessageCircle size={16} /> Consultar con {people} {people === 1 ? 'persona' : 'personas'} por WhatsApp
          </a>
          <label className="lp-range">
            <span><b>{people}</b> {people === 1 ? 'persona' : 'personas'}</span>
            <input type="range" min="1" max="15" value={people} onChange={(e) => setPeople(Number(e.target.value))} />
          </label>
          <div className="lp-calc-results">
            {PLANS.map((p, i) => (
              <div key={p.name} className={costs[i] !== null && costs[i] === best ? 'lp-calc-row lp-best' : 'lp-calc-row'}>
                <span>{p.name}</span>
                {costs[i] === null
                  ? <em>Solo para 1 persona</em>
                  : <strong>{clp(costs[i] * factor)}<small>/mes + IVA</small></strong>}
                {costs[i] !== null && costs[i] === best && <span className="lp-best-tag">Te conviene</span>}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const Pilot = () => (
  <section className="lp-section">
    <div className="lp-container">
      <div className="lp-heading lp-reveal">
        <p className="lp-kicker">Piloto de 30 días</p>
        <h2>Pruébalo con tu equipo, con tus pacientes</h2>
        <p className="lp-section-lead">Un piloto pagado y acompañado, para medir resultados reales antes de decidir.</p>
      </div>
      <ol className="lp-steps">
        {PILOT_STEPS.map((s, i) => (
          <li key={s.title} className="lp-reveal" style={{ transitionDelay: `${i * 100}ms` }}>
            <span className="lp-step-num">{i + 1}</span>
            <small>{s.when}</small>
            <h3>{s.title}</h3>
            <p>{s.text}</p>
          </li>
        ))}
      </ol>
    </div>
  </section>
);

const Faq = () => {
  const [open, setOpen] = useState(0);
  return (
    <section id="faq" className="lp-section lp-section-alt">
      <div className="lp-container lp-faq-wrap">
        <div className="lp-heading lp-reveal">
          <p className="lp-kicker">Preguntas frecuentes</p>
          <h2>Lo que nos suelen preguntar</h2>
          <p className="lp-section-lead">¿Tienes otra duda? <a href={whatsappLink('Hola, tengo una pregunta sobre Medix.')} target="_blank" rel="noreferrer">Escríbenos por WhatsApp</a>.</p>
        </div>
        <div className="lp-faq lp-reveal">
          {FAQ.map((f, i) => (
            <div key={f.q} className={open === i ? 'lp-faq-item lp-open' : 'lp-faq-item'}>
              <button type="button" aria-expanded={open === i} onClick={() => setOpen(open === i ? -1 : i)}>
                {f.q} <ChevronDown size={20} />
              </button>
              <div className="lp-faq-a"><p>{f.a}</p></div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export const Contact = ({ title = 'Veamos Medix con el flujo de tu clínica' }) => (
  <section id="contacto" className="lp-section lp-cta">
    <div className="lp-container lp-split">
      <div className="lp-reveal">
        <p className="lp-kicker">Conversemos</p>
        <h2>{title}</h2>
        <p className="lp-section-lead">
          Déjanos tus datos y te contactamos dentro de 24 horas hábiles. También puedes escribirnos directo.
        </p>
        <div className="lp-contact-links">
          <a href={whatsappLink()} target="_blank" rel="noreferrer"><MessageCircle size={20} /> +56 9 8653 2326</a>
          <a href={`mailto:${CONTACT.email}`}><Mail size={20} /> {CONTACT.email}</a>
        </div>
      </div>
      <div className="lp-card lp-form-card lp-reveal">
        <ContactForm />
      </div>
    </div>
  </section>
);

export const Footer = () => (
  <footer className="lp-footer">
    <div className="lp-container lp-footer-inner">
      <img src={medixLogo} alt="Medix" />
      <nav>
        <a href="/#funciones">Funciones</a>
        <a href="/#planes">Planes</a>
        <a href="/#faq">Preguntas</a>
        <a href={APP_URL}>Ingresar</a>
        <a href="/privacidad">Privacidad</a>
        <a href="/terminos">Términos</a>
      </nav>
      <p>© {new Date().getFullYear()} Medix · Gestión médica · Hecho en Chile</p>
    </div>
  </footer>
);

export const FloatingWhatsApp = () => (
  <a className="lp-fab" href={whatsappLink()} target="_blank" rel="noreferrer" aria-label="Escríbenos por WhatsApp">
    <MessageCircle size={26} />
  </a>
);

// Barra fija en celular con las dos acciones principales; aparece después del hero.
export const MobileCta = () => {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const onScroll = () => setShow(window.scrollY > 520);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  return (
    <div className={show ? 'lp-mobile-cta lp-show' : 'lp-mobile-cta'} aria-hidden={!show}>
      <a href="/#contacto" className="lp-btn lp-btn-primary" tabIndex={show ? 0 : -1}>Solicitar demo</a>
      <a href={whatsappLink()} className="lp-btn lp-mobile-wa" target="_blank" rel="noreferrer" tabIndex={show ? 0 : -1}>
        <MessageCircle size={18} /> WhatsApp
      </a>
    </div>
  );
};

// Brillo que sigue al cursor en las tarjetas con clase lp-spot.
export const onSpotlight = (e) => {
  const card = e.target.closest && e.target.closest('.lp-spot');
  if (!card) return;
  const r = card.getBoundingClientRect();
  card.style.setProperty('--mx', `${e.clientX - r.left}px`);
  card.style.setProperty('--my', `${e.clientY - r.top}px`);
};

// Preguntas frecuentes de la portada como datos estructurados para Google.
const HOME_LD = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: FAQ.map((f) => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })),
};

const LandingPage = () => {
  useReveal();
  usePageMeta('/', HOME_LD);
  return (
    <div className="lp" id="top" onMouseMove={onSpotlight}>
      <Header />
      <main>
        <Hero />
        <Stats />
        <Marquee />
        <FeatureTour />
        <BeforeAfter />
        <SpecialtyCards />
        <Trust />
        <Comparison />
        <Pricing />
        <Pilot />
        <Faq />
        <Contact />
      </main>
      <Footer />
      <FloatingWhatsApp />
      <MobileCta />
    </div>
  );
};

export default LandingPage;
